import { Injectable } from "@angular/core";
import { from, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { AuthHTTPService } from "./auth-http";
import { ResponseModel } from "../_models/response.model";
import { UserRegisterModel } from "../_models/user-register.model";
import { SecretWordModel } from "@modules/auth/_models/secret-word.model";
import { AppStateService } from "../../../store/app-state.service";
import {
  LANDBOT_TOKEN,
  TWILIO_USER_TOKEN_KEY,
  USER_EMAIL,
  USER_TOKEN_KEY,
} from "@shared/constants";
import { MessengerService } from "@modules/messenger/messenger.service";
import { UserDataModel } from "@modules/auth";
import { environment } from "src/environments/environment";
import { PendoService } from "../../../_services/pendo.service";
import { PushNotificationService } from "@services/PushNotificationService/PushNotification.service";
import { SecureStorageService } from "@services/SecureStorage/secure-storage.service";

export type Credentials =
  | { type: "password"; login: string; password: string }
  | { type: "token"; token: string };

@Injectable({
  providedIn: "root",
})
export class AuthService {
  currentUser$: Observable<UserDataModel> = this.appStateService.currentUser$;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor(
    private authHttpService: AuthHTTPService,
    private appStateService: AppStateService,
    private messengerService: MessengerService,
    private pendoService: PendoService,
    private pushNotificationsService: PushNotificationService,
    private secureStorage: SecureStorageService
  ) {}

  get currentUserValue(): UserDataModel {
    return this.appStateService.state.currentUser;
  }

  set currentUserValue(user: UserDataModel) {
    this.appStateService.patchState({ currentUser: user });
  }

  get isAuth(): boolean {
    return !!this.accessToken || !!this.currentUserValue?.jwToken;
  }

  login(
    login: string,
    password: string
  ): Observable<ResponseModel<UserDataModel> | undefined> {
    return this.authorizeUser({ type: "password", login, password });
  }

  logout() {
    this.secureStorage
      .clearAll()
      .catch((err) => console.error("[auth] Error clearing storage", err));

    console.log("[auth] Logging out");

    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(TWILIO_USER_TOKEN_KEY);

    this.accessToken = null;
    this.refreshToken = null;

    this.pushNotificationsService.deregister();
    this.pendoService.logout();

    this.messengerService
      .shutDown()
      .finally(() => (window.location.pathname = "/auth"));
  }

  getUserByToken(): Observable<ResponseModel<UserDataModel> | undefined> {
    const persistentJWT = localStorage.getItem(USER_TOKEN_KEY);
    if (persistentJWT) {
      return this.authorizeUser({ type: "token", token: persistentJWT });
    }

    return from(this.secureStorage.getJwtToken()).pipe(
      switchMap((token) =>
        token ? this.authorizeUser({ type: "token", token }) : of(undefined)
      )
    );
  }

  authorizeUser(
    credentials: Credentials | null
  ): Observable<ResponseModel<UserDataModel> | undefined> {
    if (!credentials) {
      return of(undefined);
    }

    const authRequest$ =
      credentials.type === "password"
        ? this.authHttpService.login(credentials.login, credentials.password)
        : this.authHttpService.getUserByToken(credentials.token);

    return authRequest$.pipe(
      switchMap((respUser) => {
        if (respUser.succeeded) {
          const user = respUser.data;

          return from(this.saveTokens(user.jwToken, user.refreshToken)).pipe(
            map(() => {
              localStorage.setItem(USER_TOKEN_KEY, user.jwToken);

              this.appStateService.patchState({ currentUser: user });
              this.messengerService.getTwilioToken(user.id).subscribe();

              const prevUser = localStorage.getItem(USER_EMAIL);
              if (prevUser !== user.email) {
                localStorage.removeItem(
                  `${LANDBOT_TOKEN}/${environment.landbotId}`
                );
                localStorage.setItem(USER_EMAIL, user.email);
              }

              this.pendoService.identifyUser(user);

              return respUser;
            })
          );
        }

        return of(respUser);
      }),

      switchMap((result) =>
        from(this.pushNotificationsService.register()).pipe(
          catchError((pushErr) => {
            console.warn("[auth] Push registration failed, login continues", pushErr);
            return of(undefined);
          }),
          map(() => result)
        )
      ),

      catchError((err) => {
        console.error("Authorization error", err);

        const { Message, message } = err?.error || {};
        const backendMessage = Message ?? message ?? err?.message;

        return of({
          succeeded: false,
          message: backendMessage,
          data: null,
          errors: null,
        } as ResponseModel<UserDataModel>);
      })
    );
  }

  private async saveTokens(
    accessToken: string,
    refreshToken: string
  ): Promise<void> {
    console.log("[auth] Saving tokens to secure storage");
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    await this.secureStorage.setJwtToken(accessToken);
    await this.secureStorage.setRefreshToken(refreshToken);

    console.log("[auth] Tokens saved successfully");
  }

  refreshAuthToken(): Observable<ResponseModel<UserDataModel>> {
    return from(this.secureStorage.getRefreshToken()).pipe(
      switchMap((refreshToken) => {
        if (!refreshToken) {
          this.logout();
          return of(undefined);
        }

        return this.authHttpService.refreshToken(refreshToken).pipe(
          switchMap((response) => {
            if (response.succeeded) {
              const user = response.data;

              return from(
                this.saveTokens(user.jwToken, user.refreshToken)
              ).pipe(
                map(() => {
                  localStorage.setItem(USER_TOKEN_KEY, user.jwToken);
                  this.appStateService.patchState({ currentUser: user });
                  return response;
                })
              );
            }

            return of(response);
          }),

          catchError((err) => {
            console.error("[auth] refresh token error", err);
            this.logout();
            throw err;
          })
        );
      })
    );
  }

  registration(
    user: UserRegisterModel
  ): Observable<ResponseModel<UserDataModel>> {
    return this.authHttpService.createUser(user).pipe(
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      })
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.authHttpService.forgotPassword(email);
  }

  update(word: string): Observable<ResponseModel<number>> {
    return this.authHttpService.updateSecretWord(word).pipe(
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      })
    );
  }

  getSecretWord(): Observable<ResponseModel<SecretWordModel>> {
    return this.authHttpService.getSecretWord().pipe(
      catchError((err) => {
        console.error("err", err);
        return of(undefined);
      })
    );
  }

  resetPassword(
    email: string,
    token: string,
    password: string,
    cPassword: string
  ): Observable<ResponseModel<string>> {
    return this.authHttpService
      .resetPassword(email, token, password, cPassword)
      .pipe(
        catchError((err) => {
          console.error("err", err);
          return of(undefined);
        })
      );
  }

  confirmEmail(param: { code: any; userId: any }) {
    return this.authHttpService.confirmEmail(param);
  }
}
